import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button } from "@mui/material";
import TextField from "@mui/material/TextField";
import React, { useEffect, useState, Dispatch, SetStateAction } from "react";

import { showError, showToast } from "../helpers/showToast";
import {
  useAddFAQMutation,
  useLazyGetFAQQuery,
  useUpdateFaqMutation,
} from "../service/cms";
import { CommonBody, FaqRes } from "../types/General";
import { generateEncryptedKeyBody } from "../utils/crypto";

interface AddFaqModalProps {
  open: boolean;
  onClose: () => void;
  getFaqList: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
  selectedFaq: any;
}

export default function AddFaq({
  open,
  onClose,
  setOpen,
  selectedId,
  selectedFaq,
  getFaqList,
}: AddFaqModalProps) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState(false);
  const [addFaqData] = useAddFAQMutation();
  const [updateFaq] = useUpdateFaqMutation();
  const [loading, setLoading] = useState(false);
  const [getAllData] = useLazyGetFAQQuery();

  useEffect(() => {
    if (open && selectedFaq) {
      setQuestion(selectedFaq.question?.en || "");
      setAnswer(selectedFaq.answer?.en || "");
    } else if (open && !selectedFaq) {
      setQuestion("");
      setAnswer("");
    }
  }, [open, selectedFaq]);

  const handleSubmit = async () => {
    setError(true);
    if (question.trim() === "") {
      showError("Question is required");
      return;
    }
    if (answer.trim() === "") {
      showError("Answer is required");
      return;
    }
    if (question.trim().length < 10) {
      showError("Minimum 10 characters are required for question");
      return;
    }
    if (answer.trim().length < 10) {
      showError("Minimum 10 characters are required for answer");
      return;
    }
    setLoading(true);
    try {
      const body = {
        question: { en: question },
        answer: { en: answer },
      };
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      let response;
      if (selectedId) {
        response = await updateFaq({
          body: encryptedBody,
          id: selectedId,
        }).unwrap();
      } else {
        response = await addFaqData({ body: encryptedBody }).unwrap();
      }
      if (response?.statusCode === 200) {
        setError(false);
        setOpen(false);
        showToast(
          selectedId ? "FAQ updated successfully" : "FAQ added successfully"
        );
        getFaqList();
        onClose();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal addNotification_modal"
      id="addFaqModal"
      aria-labelledby="addFaq-modal-title"
      aria-describedby="addFaq-modal-description"
      open={open}
      onClose={() => {
        onClose();
        setError(false);
      }}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div
            className="btn-close"
            onClick={() => {
              setQuestion("");
              setAnswer("");
              setOpen(false);
            }}
          >
            <CloseIcon />
          </div>
          <div className="modal_title">
            <h2>{selectedId ? "Edit" : "Add"} FAQ</h2>
          </div>
          <form className="form" action="">
            <div className="control_group">
              <TextField
                variant="outlined"
                placeholder="Question"
                fullWidth
                value={question}
                onChange={(e) => {
                  if (e.target.value === " " || e.target.value === ".") {
                  } else {
                    setQuestion(e.target.value);
                  }
                }}
                hiddenLabel
              />
              {error && question == "" ? (
                <h6 className="err_msg">Question is required</h6>
              ) : error && question?.length < 10 ? (
                <h6 className="err_msg">Minimum 10 characters are required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group">
              <TextField
                variant="outlined"
                placeholder="Answer"
                fullWidth
                hiddenLabel
                multiline
                value={answer}
                onChange={(e) => {
                  if (e.target.value === " " || e.target.value === ".") {
                  } else {
                    setAnswer(e.target.value);
                  }
                }}
                maxRows={5}
                minRows={5}
              />
              {error && answer == "" ? (
                <h6 className="err_msg">Answer is required</h6>
              ) : error && answer?.length < 10 ? (
                <h6 className="err_msg">Minimum 10 characters are required</h6>
              ) : (
                ""
              )}
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_highlight"
                onClick={() => {
                  setError(false);
                  setOpen(false);
                  setQuestion("");
                  setAnswer("");
                }}
              >
                Cancel
              </Button>
              <Button className="btn btn_secondary" onClick={handleSubmit}>
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
